import { supabase } from '@/integrations/supabase/client';
import { VendorApplication, VendorApplicationRow } from '@/contracts/vendor-application';

export async function submitVendorApplication(data: VendorApplication): Promise<string> {
  const { data: result, error } = await supabase
    .from('vendor_applications')
    .insert([data as any])
    .select('id')
    .single();
  
  if (error) throw error;
  
  // Notify admins via GHL
  await supabase.functions.invoke('notify-vendor-application-submitted', {
    body: { applicationId: result.id }
  }).catch(err => {
    console.error('Failed to send admin notification:', err);
    // Don't fail the application submission if notification fails
  });
  
  return result.id;
}

export async function getVendorApplications(
  status?: string
): Promise<VendorApplicationRow[]> {
  let query = supabase
    .from('vendor_applications')
    .select('*')
    .order('created_at', { ascending: false });
  
  if (status && status !== 'all') {
    query = query.eq('status', status);
  }
  
  const { data, error } = await query;
  if (error) throw error;
  return data as VendorApplicationRow[];
}

export async function updateApplicationStatus(
  id: string,
  status: string,
  reviewNotes?: string
): Promise<void> {
  const { data: user } = await supabase.auth.getUser();
  
  // Update application status
  const { data: application, error: appError } = await supabase
    .from('vendor_applications')
    .update({
      status,
      review_notes: reviewNotes,
      reviewed_at: new Date().toISOString(),
      reviewed_by: user.user?.id,
    })
    .eq('id', id)
    .select('contact_email')
    .single();
  
  if (appError) throw appError;
  
  // If approved, set is_vendor=true on the profile
  if (status === 'approved' && application?.contact_email) {
    const { error: profileError } = await supabase
      .from('profiles')
      .update({ is_vendor: true })
      .eq('email', application.contact_email);
    
    if (profileError) {
      console.error('Failed to update vendor status:', profileError);
      // Don't throw - application is approved even if vendor flag fails
    }
  }
  
  // Notify vendor of decision via GHL
  if (status === 'approved' || status === 'rejected') {
    await supabase.functions.invoke('notify-vendor-application-decision', {
      body: { applicationId: id, decision: status, reviewNotes }
    }).catch(err => {
      console.error('Failed to send vendor decision notification:', err);
      // Don't fail the status update if notification fails
    });
  }
}

export async function uploadVendorAsset(
  file: File,
  applicationId: string,
  type: 'logo' | 'screenshot'
): Promise<string> {
  const fileExt = file.name.split('.').pop();
  const fileName = `${applicationId}/${type}-${Date.now()}.${fileExt}`;
  
  const { error: uploadError } = await supabase.storage
    .from('vendor-applications')
    .upload(fileName, file);
  
  if (uploadError) throw uploadError;
  
  const { data } = supabase.storage
    .from('vendor-applications')
    .getPublicUrl(fileName);
  
  return data.publicUrl;
}
